/** ONE Smart Attach — PUBLIC one-page recommendations (token-scoped, no auth). */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const sa = axios.create({ baseURL: `${BACKEND}/api/public/smart-attach` });
const money = (a, c = "INR") => a == null ? "Price on request" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: c || "INR", maximumFractionDigits: 0 }).format(a);

export default function SmartAttachPublic() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [acted, setActed] = useState({});

  useEffect(() => {
    sa.get(`/${token}`).then(({ data }) => setD(data)).catch((e) => setErr(e.response?.data?.detail || "This link is not available."));
  }, [token]);

  const act = async (rec, action) => {
    try {
      const { data } = await sa.post(`/${token}/interest`, { recommendation_id: rec.id, action });
      setActed((s) => ({ ...s, [rec.id]: data.message || "Thank you" }));
    } catch { setActed((s) => ({ ...s, [rec.id]: "Something went wrong" })); }
  };

  if (err) return <Shell><div className="py-20 text-center text-white/70" data-testid="sa-error">{err}</div></Shell>;
  if (!d) return <Shell><div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-white/60" /></div></Shell>;

  const s = d.snapshot; const hero = s.hero;
  const Card = (r) => {
    const isService = r.group === "SERVICES";
    return (
    <div key={r.id} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 flex flex-col" data-testid={`sa-rec-${r.id}`}>
      {isService ? (
        <div className="h-24 rounded-lg bg-indigo-500/10 mb-3 grid place-items-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-300 border border-indigo-400/40 rounded-full px-2.5 py-1">Service</span>
        </div>
      ) : (
        <div className="h-24 rounded-lg bg-white/5 mb-3 grid place-items-center text-white/25">
          {r.image_url ? <img src={r.image_url} alt={r.name} className="h-full object-contain" /> : <ShoppingBag className="h-8 w-8" />}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-wide text-emerald-300/80">{r.compatibility_label}</div>
      <div className="text-sm font-semibold mt-0.5" style={{ color: "#fff" }}>{r.name}</div>
      <div className="text-lg font-bold mt-1" style={{ color: "#fff" }}>{money(r.price, r.currency)}</div>
      <div className="mt-auto pt-3">
        {acted[r.id] ? <div className="text-xs text-emerald-300" data-testid={`sa-acted-${r.id}`}>{acted[r.id]}</div> : (
          <div className="flex gap-2">
            <button onClick={() => act(r, r.cta === "REQUEST_PRICE" ? "REQUEST_PRICE" : "ADD_TO_ORDER")} data-testid={`sa-cta-${r.id}`}
              className="flex-1 h-9 rounded-lg bg-white text-slate-900 text-xs font-semibold">
              {r.cta === "REQUEST_PRICE" ? "Request price" : "Add to order"}
            </button>
            <button onClick={() => act(r, "TALK_TO_SUNTRONIX")} data-testid={`sa-talk-${r.id}`}
              className="h-9 px-3 rounded-lg border border-white/20 text-white/70 text-xs">Talk to us</button>
          </div>
        )}
      </div>
    </div>
  );
  };

  return (
    <Shell brand={d.brand_name} code={d.code}>
      <div data-testid="smart-attach-page">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-6">
          <div className="text-[11px] uppercase tracking-wide text-white/40">Thank you for your purchase</div>
          <h1 className="text-2xl font-bold mt-1" style={{ color: "#fff" }}>{hero.product_name}</h1>
          <div className="text-sm text-white/50">Order {s.order_code}{s.invoice_number ? ` \u00b7 Invoice ${s.invoice_number}` : ""}</div>
        </div>
        <h2 className="text-base font-semibold mb-3" style={{ color: "#fff" }}>Recommended for your {hero.model || hero.product_name}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sa-primary">{s.primary.map(Card)}</div>
        {s.extra && s.extra.length > 0 && (
          <>
            <h2 className="text-base font-semibold mt-6 mb-3" style={{ color: "#fff" }}>Complete your workspace</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sa-extra">{s.extra.map(Card)}</div>
          </>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children, brand, code }) {
  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white">
      <div className="border-b border-white/10"><div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-400" />
        <div><div className="font-semibold">{brand || "SUNTRONIX"}</div>{code && <div className="text-[11px] text-white/40 font-mono">{code}</div>}</div>
      </div></div>
      <div className="max-w-4xl mx-auto px-4 py-8">{children}</div>
    </div>
  );
}
